import { useState } from "react";
import { Outlet, useOutletContext, useLocation } from "react-router";
import { Sidebar } from "./Sidebar";
import { ThemeToggle } from "./ThemeToggle";
import { TimeRangeSelector, useTimeRange, type TimeRange } from "./TimeRangeSelector";
import { UserFilter } from "./UserFilter";
import { OnboardingGuide } from "./OnboardingGuide";
import { OnboardingWizard } from "./OnboardingWizard";
import { PausedBanner } from "./PausedBanner";
import { FreeWeekBanner } from "./FreeWeekBanner";
import { CardNeededBanner } from "./CardNeededBanner";
import { useAuth } from "@/contexts/AuthContext";
import { useUsers } from "@/api/hooks/use-users";

// Routes whose KPIs respond to the time-range and per-user filters. The filter
// bar is hidden everywhere else (Governance, Billing, Requests, etc.).
const FILTERED_ROUTES = new Set(["/", "/routing", "/savings"]);

export interface LayoutContext {
  tenantId: string;
  range: TimeRange;
  userId: string;
}

export function useLayoutContext() {
  return useOutletContext<LayoutContext>();
}

const ROUTE_TITLES: Record<string, string> = {
  "/": "Overview",
  "/requests": "Requests",
  "/routing": "Routing",
  "/routing/dry-run": "Routing · Dry Run",
  "/savings": "Savings",
  "/billing": "Billing",
  "/governance": "Governance",
  "/prompt-stability": "Prompt Stability",
};

function deriveBreadcrumb(pathname: string): string {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  // Match nested patterns like /routing/:id
  if (pathname.startsWith("/routing/")) return "Routing · Policy";
  return "";
}

export function Layout() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? "";
  const { fromDate, toDate, setFromDate, setToDate, range } = useTimeRange();
  const [userId, setUserId] = useState("");
  const location = useLocation();
  const crumb = deriveBreadcrumb(location.pathname);

  const usersQ = useUsers(tenantId);
  const userIds = usersQ.data?.users.map((u) => u.external_user_id) ?? [];
  // Per-user controls only appear once a tenant has 2+ distinct end-users.
  const showUserFilter = userIds.length >= 2;
  const showFilters = FILTERED_ROUTES.has(location.pathname);

  const ctx: LayoutContext = { tenantId, range, userId };

  return (
    <div className="flex h-screen overflow-hidden bg-canvas">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="relative z-10 flex items-center justify-between border-b border-line-subtle bg-surface/80 px-6 py-3 backdrop-blur-sm">
          <div className="flex items-center gap-2 text-xs text-fg-muted">
            <span className="font-medium tracking-wide uppercase text-fg-faint">
              Phantm
            </span>
            {crumb && (
              <>
                <span aria-hidden="true" className="text-fg-faint/70">/</span>
                <span className="text-fg">{crumb}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showFilters && (
              <>
                {showUserFilter && (
                  <UserFilter users={userIds} value={userId} onChange={setUserId} />
                )}
                <TimeRangeSelector
                  fromDate={fromDate}
                  toDate={toDate}
                  onFromChange={setFromDate}
                  onToChange={setToDate}
                />
                <span aria-hidden="true" className="mx-1 h-5 w-px bg-line-subtle" />
              </>
            )}
            <ThemeToggle />
          </div>
        </header>
        <main className="canvas-ambient flex-1 overflow-y-auto bg-canvas p-6">
          <PausedBanner />
          <FreeWeekBanner />
          <CardNeededBanner />
          {location.pathname === "/" && <OnboardingWizard />}
          <Outlet context={ctx} />
        </main>
      </div>
      <OnboardingGuide />
    </div>
  );
}
